import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import CTASection from "@/components/site/CTASection";
import { GLOSSARY_TERMS } from "@/data/glossary";

export default function Glossary() {
  const location = useLocation();

  // Scroll to the targeted term if the URL contains a hash anchor.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.hash]);

  // DefinedTerm JSON-LD for SEO — gives Google a structured glossary.
  useEffect(() => {
    const data = {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      name: "UKTDA Railway Glossary",
      hasDefinedTerm: GLOSSARY_TERMS.map((t) => ({
        "@type": "DefinedTerm",
        "@id": `https://traindriveracademy.com/glossary#${t.id}`,
        name: t.term,
        description: t.long,
        inDefinedTermSet: "https://traindriveracademy.com/glossary",
      })),
    };
    let el = document.getElementById("glossary-schema");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "glossary-schema";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const f = document.getElementById("glossary-schema");
      if (f) f.remove();
    };
  }, []);

  return (
    <>
      <SEO
        title="Railway Glossary | UKTDA — NTS, Human Factors, RIS-3751-TOM, Competence"
        description="A plain-English glossary of UK railway training and competence terms — including NTS, human factors, fatigue, RIS-3751-TOM, operational thinking, competence management and incident investigation."
        path="/glossary"
      />
      <PageHero
        eyebrow="Reference"
        title="A plain-English glossary of UK railway training and competence terms."
        intro="The language of safety-critical rail can be dense. This glossary explains the terms used across our site, our brands, and the wider industry."
      />

      <section className="py-16 md:py-20 bg-white" data-testid="glossary-page">
        <div className="container-rail grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-16">
          {/* Sticky alphabetical index */}
          <aside className="lg:sticky lg:top-24 self-start" data-testid="glossary-toc">
            <p className="label-eyebrow text-safety">Jump to</p>
            <ul className="mt-4 space-y-1.5">
              {GLOSSARY_TERMS.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    data-testid={`glossary-toc-${t.id}`}
                    className="text-sm text-slate-600 hover:text-safety transition-colors"
                  >
                    {t.term}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <div className="space-y-10">
            {GLOSSARY_TERMS.map((t) => {
              const related = t.related
                ? GLOSSARY_TERMS.filter((r) => t.related.includes(r.id))
                : [];
              return (
                <article
                  key={t.id}
                  id={t.id}
                  className="scroll-mt-24 border-b border-slate-200 pb-10 last:border-0"
                  data-testid={`glossary-term-${t.id}`}
                >
                  <p className="label-eyebrow text-safety">Term</p>
                  <h2 className="mt-2 font-display font-bold text-navy text-2xl md:text-3xl">
                    {t.term}
                  </h2>
                  <p className="mt-3 text-[15px] text-slate-700 leading-relaxed font-medium">
                    {t.short}
                  </p>
                  <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-3xl">
                    {t.long}
                  </p>
                  {related.length > 0 && (
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                      <span className="label-eyebrow text-slate-500 mr-2">Related</span>
                      {related.map((r) => (
                        <a
                          key={r.id}
                          href={`#${r.id}`}
                          data-testid={`glossary-related-${t.id}-${r.id}`}
                          className="text-xs text-navy bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded-md transition-colors"
                        >
                          {r.term}
                        </a>
                      ))}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow="Apply it"
        title="Want training that puts these terms into practice?"
        intro="From NTS workshops to instructor development and bespoke competence support — we can help."
        primary={{ label: "Explore training", to: "/training", testId: "glossary-cta-training" }}
        secondary={{ label: "Discuss a programme", to: "/contact", testId: "glossary-cta-contact" }}
      />
    </>
  );
}
